/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { VisionApplication } from '../../application';
import { FormElements } from '../../components/form';
import { Measurement } from '../../components/measurement';
import { TimeSelect } from '../../components/timeselect';
import { SVGIcons } from '../../map/svg';
import { IdleDTO, TripDTO } from '../../service/trip.service';
import { VisionFleetTab } from './fleet';

/** Class to manage the display of a single trip on the map, including start/end markers, polyline, and idle markers.
 *
 */
class AssetTripDisplay {
    startMarker!: google.maps.marker.AdvancedMarkerElement;
    endMarker!: google.maps.marker.AdvancedMarkerElement;
    idleMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    polyline!: google.maps.Polyline;
    bounds!: google.maps.LatLngBounds;

    static infoWindow: google.maps.InfoWindow | null;

    constructor(
        public trip: TripDTO,
        public map: google.maps.Map,
        public assetName: string
    ) {
        this.createStartMarker();
        this.createEndMarker();
        this.createPolyline();
        this.createIdleMarkers();
        this.updateBounds();
    }

    private createStartMarker() {
        const startImg = document.createElement('img');
        startImg.src = '/icons/green_flag.png';
        startImg.style.width = '32px';
        startImg.style.height = '32px';
        startImg.style.objectFit = 'contain';
        this.startMarker = new google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: { lat: this.trip.coordinates[0][1], lng: this.trip.coordinates[0][0] },
            title: 'Start',
            content: startImg,
        });
    }

    private createEndMarker() {
        const lastPoint = this.trip.coordinates[this.trip.coordinates.length - 1];
        const endImg = document.createElement('img');
        endImg.src = '/icons/checkered-flag.png';
        endImg.style.width = '32px';
        endImg.style.height = '32px';
        endImg.style.objectFit = 'contain';
        this.endMarker = new google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: { lat: lastPoint[1], lng: lastPoint[0] },
            title: 'End',
            content: endImg,
        });
    }

    private createPolyline() {
        this.polyline = new google.maps.Polyline({
            map: this.map,
            path: this.trip.coordinates.map(([lng, lat]) => ({ lat, lng })),
            icons: [
                {
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 3,
                        strokeColor: '#07da57',
                    },
                    offset: '100%',
                    repeat: '120px',
                },
            ],
            strokeColor: '#c50af3',
            strokeOpacity: 4.0,
            strokeWeight: 8,
        });

        /*
        // Commented out for now, since it conflicts with the idle marker click events.
        this.polyline.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;

            if (!AssetTripDisplay.infoWindow) {
                AssetTripDisplay.infoWindow = new google.maps.InfoWindow();
            }

            AssetTripDisplay.infoWindow.setContent(`
                <div style="
                font-family: Arial, sans-serif;
                font-size: 13px;
                padding: 6px 8px;
                font-weight: 600;
            ">
                ${this.assetName}
            </div>
            `);

            AssetTripDisplay.infoWindow.setPosition(e.latLng);
            AssetTripDisplay.infoWindow.open(this.map);
        });

        this.polyline.addListener('mouseout', () => {
            if (AssetTripDisplay.infoWindow) {
                AssetTripDisplay.infoWindow.close();
            }
        });
        */
    }

    private createIdleMarkers() {
        if (this.trip.idles) {
            this.trip.idles.forEach((idle) => {
                const idleImg = document.createElement('img');
                idleImg.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.idle('#0000FF', 20, 20));
                idleImg.style.width = '32px';
                idleImg.style.height = '32px';
                idleImg.style.objectFit = 'contain';

                const idleMarker = new google.maps.marker.AdvancedMarkerElement({
                    map: this.map,
                    position: { lat: idle.latitude, lng: idle.longitude },
                    title: `Idle for ${idle.durationSeconds} seconds`,
                    content: idleImg,
                });
                this.idleMarkers.push(idleMarker);

                idleMarker.addListener('click', () => {
                    const infoWindow = new google.maps.InfoWindow({
                        content: this.formatIdleContent(idle),
                    });
                    infoWindow.open(this.map, idleMarker);
                });
            });
        }
    }

    private updateBounds() {
        this.bounds = new google.maps.LatLngBounds();
        this.trip.coordinates.forEach(([lng, lat]) => {
            this.bounds.extend({ lat, lng });
        });
    }

    public clear() {
        this.startMarker.map = null;
        this.endMarker.map = null;
        this.polyline.setMap(null);
        this.idleMarkers.forEach((marker) => (marker.map = null));
    }

    private formatIdleContent(idle: IdleDTO) {
        const content = `
        <div style="
            font-family: Arial, sans-serif;
            background: white;
            padding: 10px 14px;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            font-size: 13px;
            line-height: 1.4;
            min-width: 180px;
        ">
            <div style="
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 6px;
            color: #1f2937;
            ">
            ${this.assetName}
            </div>

            <div style="color: #374151;">
            <span style="font-weight: 500;">Idle Duration:</span>
            ${(idle.durationSeconds / 60.0).toFixed(2)} minutes
            </div>

            <div style="
            margin-top: 4px;
            font-size: 12px;
            color: #6b7280;
            ">
            ${new Date(idle.startTimeUTC).toLocaleString()}
            </div>
        </div>
        `;
        return content;
    }
}

//=====================================================================================================================

/** Main class for the Trips tab, which allows users to view and analyze trips taken by their assets, including idle times.
 * Users can select a time range and asset group, then view a list of trips with details such as start/end times, duration, distance, and idle time.
 * Selecting a trip will display it on the map with markers for the start/end points and idles.
 */
export class FleetTabTrips {
    parent: VisionFleetTab;

    // UI Elements
    layout!: dhtmlXLayoutObject;
    paramLayout!: dhtmlXLayoutObject;
    tripGrid!: dhtmlXGridObject;
    assetGrid!: dhtmlXGridObject;
    toolbar!: dhtmlXToolbarObject;
    detailsToolbar!: dhtmlXToolbarObject;
    paramForm!: dhtmlXForm;

    map!: google.maps.Map;
    app: VisionApplication;
    loaded = false;

    selectedGroup = '';

    // Chache to store the map display objects for each trip, so they can be easily shown/hidden when the user interacts with the grid.
    assetTrips!: Map<string, Map<string, AssetTripDisplay>>; // assetId -> tripId -> display

    constructor(parent: VisionFleetTab) {
        this.parent = parent;
        this.app = parent.app;
        this.parent.dataTabbar.addTab('trips', 'Trips', 150, 3, false, false);

        this.assetTrips = new Map<string, Map<string, AssetTripDisplay>>();
        this.buildUI();
    }

    /** Called with the tab is selected.
     *  Will initialize the map if it hasn't been loaded yet, to improve performance when the user is not using the Trips tab.
     */
    public onSelect() {
        // Only load the map when the tab is selected to improve performance
        if (this.loaded == false) {
            this.map = this.layout.cells('b').attachMap({
                center: { lat: this.parent.app.organizationService.dto?.latitude, lng: this.parent.app.organizationService.dto?.longitude },
                zoom: 8,
                mapId: 'dad62ca70e7e3c4b6f8cbf8c',
            });
            this.loaded = true;
        }
        console.log('Trips Tab Selected');
    }

    private buildUI() {
        // Main layout with 3 columns: parameters on the left, map in the middle, and trip details on the bottom.
        this.layout = this.parent.dataTabbar.cells('trips').attachLayout('3L');
        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();
        this.layout.cells('c').hideHeader();

        // Details toolbar and grid in the bottom cell to export the data.
        this.detailsToolbar = this.layout.cells('c').attachToolbar();
        this.detailsToolbar.addButton('export_xls', 1, 'Excel', 'icons/excel.png', '');
        this.detailsToolbar.addButton('export_pdf', 2, 'PDF', 'icons/pdf.png', '');

        this.tripGrid = this.layout.cells('c').attachGrid();
        this.tripGrid.setImagesPath('css/imgs/');
        this.tripGrid.setHeader('Display, Asset,Start,Location,End,Location,Duration,Idle,Distance', '', [
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
        ]);
        this.tripGrid.setColTypes('ch,ro,ro,ro,ro,ro,ro,ro,ro');
        this.tripGrid.setColSorting('str,str,str,str,str,str,str,str,str');
        this.tripGrid.setColAlign('center,center,center,center,center,center,center,center,center');
        this.tripGrid.init();

        this.paramLayout = this.layout.cells('a').attachLayout('2E');
        this.paramLayout.cells('a').hideHeader();
        this.paramLayout.cells('a').fixSize(false, true);
        this.paramLayout.cells('a').setHeight(200);
        this.paramLayout.cells('a').setWidth(400);

        this.toolbar = this.paramLayout.cells('b').attachToolbar();
        this.paramLayout.cells('b').setText('Trips');
        this.toolbar.setIconsPath('css/imgs/');
        this.toolbar.addButtonSelect('asset_groups', 1, 'Groupings', [], '', '', false, false, 0, '');

        this.assetGrid = this.paramLayout.cells('b').attachGrid();
        this.assetGrid.setImagesPath('css/imgs/');
        this.assetGrid.setHeader(',Asset Name, Trip Count, Distance, Duration', '', ['text-align:center', 'text-align:center', 'text-align:center', 'text-align:center', 'text-align:center']);
        this.assetGrid.setColTypes('ch,ro,ro,ro,ro');
        this.assetGrid.setColSorting('str,str,int,int,int');
        this.assetGrid.setColAlign('center,center,center,center,center');
        this.assetGrid.init();

        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM_NO_PERIOD));
        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search'), { type: 'newcolumn' }, FormElements.button('cancel', 'Cancel', 'cancel')],
        });

        this.paramForm = this.paramLayout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('cancel');

        this.paramForm.setItemValue(
            TimeSelect.START,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            localStorage.getItem('fleetTripsStart') ? new Date(parseInt(localStorage.getItem('fleetTripsStart')!)) : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        );
        this.paramForm.setItemValue(
            TimeSelect.END,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            localStorage.getItem('fleetTripsEnd') ? new Date(parseInt(localStorage.getItem('fleetTripsEnd')!)) : new Date()
        );

        this.app.assetFolderService.folderCache?.forEach((folder) => {
            this.toolbar.addListOption('asset_groups', folder.id, 1, 'button', folder.name, '');
            this.toolbar.clearListOptionImage('asset_groups', folder.id);
        });

        if (localStorage.getItem('selected_folder')) {
            const selectedFolder = localStorage.getItem('selected_folder') as string;
            this.toolbar.setListOptionSelected('asset_groups', selectedFolder);
            const name = this.app.assetFolderService.folderCache?.get(selectedFolder)?.name;
            if (name) {
                this.paramLayout.cells('b').setText('Trips - ' + name);
            }
            this.selectedGroup = selectedFolder;
            this.populateAssetGrid();
        }

        this.setupEventHandlers();
    }

    private setupEventHandlers() {
        this.toolbar.attachEvent('onClick', (id) => {
            this.selectedGroup = id;
            const name = this.app.assetFolderService.folderCache?.get(id)?.name;
            if (name) {
                this.paramLayout.cells('b').setText('Trips - ' + name);
            }
            this.populateAssetGrid();
        });

        // Search button handler to query data.
        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'search': {
                    this.getTripData();
                    break;
                }
            }
        });

        // Click on a row to highlight the trip on the map. Only works if the checkbox is selected, to allow users to select trips without changing the map display.
        this.tripGrid.attachEvent('onRowSelect', (rowId) => {
            if (this.tripGrid.getCheckedRows(0).split(',').includes(rowId)) {
                const assetId = this.tripGrid.getUserData(rowId, 'assetId');
                const tripDisplay = this.assetTrips.get(assetId)?.get(rowId);

                if (tripDisplay) {
                    this.map.panTo(tripDisplay.bounds.getCenter());
                    tripDisplay.polyline.setOptions({ strokeColor: '#FF0000' });
                    setTimeout(() => {
                        tripDisplay.polyline.setOptions({ strokeColor: '#c50af3' });
                    }, 1400);
                }
            }
        });

        // Checkbox handler to show/hide the trip on the map.
        this.tripGrid.attachEvent('onCheck', (rowId, cellIndex, state) => {
            const assetId = this.tripGrid.getUserData(rowId, 'assetId');
            const tripDisplay = this.assetTrips.get(assetId)?.get(rowId);
            if (tripDisplay) {
                if (state) {
                    tripDisplay.startMarker.map = this.map;
                    tripDisplay.endMarker.map = this.map;
                    tripDisplay.polyline.setMap(this.map);
                    tripDisplay.idleMarkers.forEach((marker) => (marker.map = this.map));
                } else {
                    tripDisplay.startMarker.map = null;
                    tripDisplay.endMarker.map = null;
                    tripDisplay.polyline.setMap(null);
                    tripDisplay.idleMarkers.forEach((marker) => (marker.map = null));
                }
            }
        });

        // Header checkbox to select/deselect all trips for an asset.

        this.assetGrid.attachEvent('onHeaderClick', (index) => {
            if (index === 0) {
                const allChecked = this.assetGrid.getCheckedRows(0).split(',').length === this.assetGrid.getRowsNum();
                for (let i = 0; i < this.assetGrid.getRowsNum(); i++) {
                    const rowId = this.assetGrid.getRowId(i);
                    this.assetGrid.cells(rowId, 0).setValue(allChecked ? '0' : '1');
                }
            } else {
                return true;
            }
        });

        // Details toolbar handlers to export the currently displayed trips in either PDF or Excel format.
        this.detailsToolbar.attachEvent('onClick', (id) => {
            switch (id) {
                case 'export_xls': {
                    this.getReport('xlsx');
                    break;
                }
                case 'export_pdf': {
                    this.getReport('pdf');
                    break;
                }
            }
        });
    }

    private getReport(format: 'pdf' | 'xlsx') {
        const start = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const end = this.paramForm.getItemValue(TimeSelect.END).getTime();
        const assets = this.assetGrid.getCheckedRows(0).split(',');

        if (assets.length === 0) {
            dhtmlx.message({ type: 'error', text: 'Please select at least one asset.' });
            return;
        }

        if (format == 'pdf') {
            this.app.tripService.downloadPDFPost('/trip/report?start=' + start + '&end=' + end + '&format=pdf', `trip_report_${Date.now()}.${format}`, assets, () => {
                /* empty */
            });
        } else if (format == 'xlsx') {
            this.app.tripService.downloadXLSXPost('/trip/report?start=' + start + '&end=' + end + '&format=xlsx', `trip_report_${Date.now()}.${format}`, assets, () => {
                /* empty */
            });
        }
    }

    private getTripData() {
        const start = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const end = this.paramForm.getItemValue(TimeSelect.END).getTime();

        if (!start || !end) {
            dhtmlx.message({ type: 'error', text: 'Please select both start and end times.' });
            return;
        }

        const assets = this.assetGrid.getCheckedRows(0).split(',');
        if (assets.length === 1 && assets[0] === '') {
            dhtmlx.message({ type: 'error', text: 'Please select at least one asset.' });
            return;
        }

        this.app.tripService.fetchTripData(start, end, assets).then((data) => {
            this.tripGrid.clearAll(false);
            this.clearAssetTripCache();

            data.parsedBody?.forEach((trip) => {
                this.updateAssetTripCache(trip);

                const asset = this.app.assetService.assetList.get(trip.assetId);
                const assetName = asset ? asset.id.name : trip.assetId;
                const startTime = new Date(trip.startTimeUTC).toLocaleString();
                const endTime = new Date(trip.endTimeUTC).toLocaleString();
                const durationSeconds = Math.floor((trip.endTimeUTC - trip.startTimeUTC) / 1000);
                const durationFormatted = new Date(durationSeconds * 1000).toISOString().substr(11, 8);
                const distanceFormatted = Measurement.km2miles(trip.distanceKm).toFixed(2) + ' mi';

                const idleSeconds = Math.abs(trip.idles.reduce((sum, idle) => sum + idle.durationSeconds, 0));
                const idleFormatted = (idleSeconds / 60.0).toFixed(2) + ' min';

                const startLocation = trip.startAddress ? trip.startAddress.housenumber + ' ' + trip.startAddress.addressStreet : '';
                const endLocation = trip.endAddress ? trip.endAddress.housenumber + ' ' + trip.endAddress.addressStreet : '';

                this.tripGrid.addRow(trip.id, ['1', assetName, startTime, startLocation, endTime, endLocation, durationFormatted, idleFormatted, distanceFormatted], 0, '', '', false);
                this.tripGrid.setUserData(trip.id, 'assetId', trip.assetId);
            });
        });
    }

    private updateAssetTripCache(trip: TripDTO) {
        if (!this.assetTrips.has(trip.assetId)) {
            this.assetTrips.set(trip.assetId, new Map<string, AssetTripDisplay>());
        }
        const assetTripMap = this.assetTrips.get(trip.assetId);
        if (assetTripMap) {
            const assetName = this.app.assetService.assetList.get(trip.assetId)?.id.name || trip.assetId;
            assetTripMap.set(trip.id, new AssetTripDisplay(trip, this.map, assetName));
        }

        this.assetGrid.cells(trip.assetId, 2).setValue((assetTripMap?.size || 0).toString());
        const totalDistanceKm = Array.from(assetTripMap?.values() || []).reduce((sum, display) => sum + display.trip.distanceKm, 0);
        const totalDurationSeconds = Array.from(assetTripMap?.values() || []).reduce((sum, display) => sum + (display.trip.endTimeUTC - display.trip.startTimeUTC) / 1000, 0);
        const distanceFormatted = Measurement.km2miles(totalDistanceKm).toFixed(2) + ' mi';
        const durationFormatted = new Date(totalDurationSeconds * 1000).toISOString().substr(11, 8);
        this.assetGrid.cells(trip.assetId, 3).setValue(distanceFormatted);
        this.assetGrid.cells(trip.assetId, 4).setValue(durationFormatted);
    }

    private clearAssetTripCache() {
        this.assetTrips.forEach((assetTripMap) => {
            assetTripMap.forEach((assetTripDisplay) => {
                assetTripDisplay.clear();
            });
        });
        this.assetTrips.clear();
    }

    private populateAssetGrid() {
        this.assetGrid.clearAll(false);
        this.app.assetFolderService.folderCache?.get(this.selectedGroup)?.items.forEach((item) => {
            const assetId = item.id;
            this.assetGrid.addRow(assetId, ['1', item.name, '', '', ''], 0, '', '', false);
            if (item.name.startsWith('#')) {
                this.assetGrid.setUserData(item.id, 'name', item.name.substring(1));
            } else {
                this.assetGrid.setUserData(item.id, 'name', item.name);
            }
        });
    }
}
